/**
 * 소요 분출용량 계산기 — 화면 상태와 실행.
 *
 * API 521 에 산정 근거가 있는 세 시나리오만 계산기를 붙인다.
 *   13. 액체 열팽창      (4.4.12.3, 식 2)
 *   14. 외부 화재        (4.4.13, 식 7/8 · 식 9~12)
 *   15. 열교환기 튜브 파열 (4.4.14.2.2 — 표준이 지시한 유동식)
 *
 * 나머지 14개는 표준이 공정 물질수지·동특성 해석으로 구하라고만 하므로
 * 계산식이 존재하지 않는다. 그쪽은 계속 직접 입력이다.
 *
 * 이 파일은 React 를 import 하지 않는다 — 화면 없이 시험할 수 있어야 한다.
 */
import {
  fireUnwettedRequiredArea,
  fireWettedRelievingRate,
  horizontalCylinderWettedArea,
  sphereWettedArea,
  verticalCylinderWettedArea,
} from "../calc/fireCase";
import {
  thermalExpansionRelievingRate,
} from "../calc/thermalExpansion";
import {
  tubeRuptureGasRate,
  tubeRuptureLiquidRate,
} from "../calc/tubeRupture";
import {
  pipingVelocityInflowRate,
  positiveDisplacementRelievingRate,
  valveFailureGasRate,
  valveFailureLiquidRate,
} from "../calc/maxInflow";
import { cvValveFlowGas, cvValveFlowLiquid } from "../calc/controlValveCv";
import { pipeInflowFromPressureDifference } from "../calc/pipeInflow";
import {
  coefficientC,
  criticalPressureRatio,
  relievingPressureKpaAbs,
  subcriticalCoefficientF2,
} from "../calc/gas";
import {
  allowableOverpressurePercent,
  type ContingencyKind,
  type InstallationKind,
} from "../calc/governingCase";
import { num } from "./formBits";
import { ATMOSPHERIC_KPA, convertInput, toKpa, type PressureUnit } from "./pressureUnit";
import type { SimpleFluidKind } from "./caseState";

/** 계산기를 붙인 시나리오 번호. */
export const RATE_CALC_ITEMS: ReadonlySet<number> = new Set([
  1, 8, 9, 10, 13, 14, 15,
]);

export type RateCalcKind =
  | "pdPumpLiquid"
  | "pdPumpGas"
  | "pipingVelocity"
  | "pipeHydraulics"
  | "valveFailureLiquid"
  | "valveFailureGas"
  | "cvValveLiquid"
  | "cvValveGas"
  | "fireWetted"
  | "fireUnwetted"
  | "thermal"
  | "tubeLiquid"
  | "tubeGas";

export const RATE_CALC_LABELS: Record<RateCalcKind, string> = {
  pdPumpLiquid: "정변위 펌프 최대유입 — 액체",
  pdPumpGas: "정변위 압축기 최대유입 — 기체",
  pipingVelocity: "배관 최대유입 — 표준유속 기준(약식)",
  pipeHydraulics: "배관 최대유입 — 배관 수력학 역산",
  valveFailureLiquid: "밸브 고장 — 배관 오리피스 근사(액체)",
  valveFailureGas: "밸브 고장 — 배관 오리피스 근사(기체)",
  cvValveLiquid: "밸브 고장 — Cv 정식 계산(액체)",
  cvValveGas: "밸브 고장 — Cv 정식 계산(기체)",
  fireWetted: "화재 — 액체가 있는 용기 (식 7/8)",
  fireUnwetted: "화재 — 기체만 있는 용기 (식 9)",
  thermal: "액체 열팽창 (식 2)",
  tubeLiquid: "튜브 파열 — 플래싱 없는 액체",
  tubeGas: "튜브 파열 — 기체",
};

/** 시나리오 번호별로 고를 수 있는 계산기. */
export function kindsForItem(item: number): readonly RateCalcKind[] {
  switch (item) {
    case 1:
    case 8:
      return ["pdPumpLiquid", "pdPumpGas", "pipeHydraulics", "pipingVelocity"];
    case 9:
    case 10:
      return [
        "cvValveLiquid",
        "cvValveGas",
        "valveFailureLiquid",
        "valveFailureGas",
      ];
    case 13:
      return ["thermal"];
    case 14:
      return ["fireWetted", "fireUnwetted"];
    case 15:
      return ["tubeLiquid", "tubeGas"];
    default:
      return [];
  }
}

export type WettedGeometry = "manual" | "vertical" | "horizontal" | "sphere";

/**
 * 계산기 입력. 다섯 계산기가 한 덩어리를 나눠 쓴다.
 * 필드를 분리하지 않은 이유는 저장·불러오기와 단위 환산을 한 곳에서
 * 처리하기 위해서다. 쓰지 않는 필드는 그냥 무시된다.
 */
export interface RateCalcState {
  open: boolean;
  kind: RateCalcKind;
  /**
   * '소요 분출용량에 넣기'로 실제로 옮긴 값. 빈 문자열이면 계산기를 쓰지
   * 않았다는 뜻이다.
   *
   * 이걸 따로 남기는 이유: 계산서에 산정 근거를 실을지 말지를 정하고,
   * **넣은 뒤 사용자가 유량을 손으로 고쳤는지**를 알아내기 위해서다.
   * 근거와 값이 어긋난 계산서가 나가면 안 된다.
   */
  appliedFlow: string;

  /* 화재 — 액체 */
  geometry: WettedGeometry;
  diameter: string;
  length: string;
  liquidHeight: string;
  wettedArea: string;
  environmentFactor: string;
  insulated: boolean;
  drainage: boolean;
  latentHeat: string;

  /* 화재 — 기체 */
  exposedArea: string;
  /** 정상 운전 압력 — 사용자 단위의 게이지압 */
  normalPressure: string;
  normalTemperature: string;
  wallTemperature: string;
  useDefaultFPrime: boolean;

  /* 액체 열팽창 */
  alphaV: string;
  heatRate: string;
  relativeDensity: string;
  specificHeat: string;

  /* 정변위 펌프/압축기 최대유입 */
  pdRatedFlow: string;
  pdDensity: string;
  /** 흡입측 압력 — 사용자 단위 게이지압 (기체) */
  pdSuctionPressure: string;
  pdSuctionTemperature: string;
  pdMolecularWeight: string;
  pdCompressibility: string;
  /** 표준 근거 없는 사용자 지정 계수. 기본 "1" */
  pdCapacityFactor: string;

  /* 배관 최대유입 — 표준유속 기준 */
  pipeInnerDiameter: string;
  pipeVelocity: string;
  pipeDensity: string;

  /* 배관 최대유입 — 배관 수력학 역산 */
  phInnerDiameter: string;
  phStraightLength: string;
  phFittingLength: string;
  phRoughness: string;
  /** 상류(구동측) 압력 — 사용자 단위 게이지압 */
  phUpstreamPressure: string;
  /** 하류(받는측) 압력 — 사용자 단위 게이지압 */
  phDownstreamPressure: string;
  phDensity: string;
  /** 점도 (cP) */
  phViscosity: string;

  /* 밸브 고장 — Cv 정식 계산 */
  cvValue: string;
  /** 상류(감압 전) 압력 — 사용자 단위 게이지압 */
  cvUpstreamPressure: string;
  /** 하류(감압 후) 압력 — 사용자 단위 게이지압 */
  cvDownstreamPressure: string;
  cvDensity: string;
  cvFL: string;
  cvVaporPressure: string;
  cvCriticalPressure: string;
  cvTemperature: string;
  cvMolecularWeight: string;
  cvSpecificHeatRatio: string;
  cvCompressibility: string;
  cvXt: string;

  /* 제어밸브·체크밸브 고장 */
  valveInnerDiameter: string;
  /** 감압 전(상류) 압력 — 사용자 단위 게이지압 */
  valveUpstreamPressure: string;
  /** 감압 후(하류) 압력 — 사용자 단위 게이지압 */
  valveDownstreamPressure: string;
  valveDensity: string;
  valveCd: string;
  valveTemperature: string;
  valveMolecularWeight: string;
  valveSpecificHeatRatio: string;
  valveCompressibility: string;

  /* 튜브 파열 */
  tubeId: string;
  /** 고압측 압력 — 사용자 단위의 게이지압 */
  highSidePressure: string;
  tubeDensity: string;
  tubeCd: string;
  tubeTemperature: string;
  tubeMolecularWeight: string;
  tubeSpecificHeatRatio: string;
  tubeCompressibility: string;
}

/** 단위를 바꿀 때 함께 환산해야 하는 압력 입력들. */
export const RATE_CALC_PRESSURE_FIELDS = [
  "normalPressure",
  "highSidePressure",
  "pdSuctionPressure",
  "phUpstreamPressure",
  "phDownstreamPressure",
  "cvUpstreamPressure",
  "cvDownstreamPressure",
  "valveUpstreamPressure",
  "valveDownstreamPressure",
] as const satisfies readonly (keyof RateCalcState)[];

export function newRateCalc(item = 14): RateCalcState {
  return {
    open: false,
    kind: kindsForItem(item)[0] ?? "fireWetted",
    appliedFlow: "",

    geometry: "vertical",
    diameter: "3",
    length: "6",
    liquidHeight: "4",
    wettedArea: "",
    environmentFactor: "1",
    insulated: false,
    drainage: true,
    latentHeat: "350000",

    exposedArea: "40",
    normalPressure: "700",
    normalTemperature: "320",
    wallTemperature: "866",
    useDefaultFPrime: false,

    alphaV: "0.0009",
    heatRate: "100000",
    relativeDensity: "0.8",
    specificHeat: "2100",

    pdRatedFlow: "10",
    pdDensity: "800",
    pdSuctionPressure: "0",
    pdSuctionTemperature: "320",
    pdMolecularWeight: "28",
    pdCompressibility: "1",
    pdCapacityFactor: "1",

    pipeInnerDiameter: "100",
    pipeVelocity: "3",
    pipeDensity: "800",

    phInnerDiameter: "150",
    phStraightLength: "20",
    phFittingLength: "5",
    phRoughness: "0.045",
    phUpstreamPressure: "600",
    phDownstreamPressure: "0",
    phDensity: "800",
    phViscosity: "0.5",

    cvValue: "50",
    cvUpstreamPressure: "600",
    cvDownstreamPressure: "0",
    cvDensity: "800",
    cvFL: "0.9",
    cvVaporPressure: "0",
    cvCriticalPressure: "",
    cvTemperature: "320",
    cvMolecularWeight: "28",
    cvSpecificHeatRatio: "1.3",
    cvCompressibility: "1",
    cvXt: "0.7",

    valveInnerDiameter: "50",
    valveUpstreamPressure: "600",
    valveDownstreamPressure: "0",
    valveDensity: "800",
    valveCd: "1",
    valveTemperature: "320",
    valveMolecularWeight: "28",
    valveSpecificHeatRatio: "1.3",
    valveCompressibility: "1",

    tubeId: "19.86",
    highSidePressure: "5000",
    tubeDensity: "850",
    tubeCd: "0.61",
    tubeTemperature: "400",
    tubeMolecularWeight: "16.04",
    tubeSpecificHeatRatio: "1.31",
    tubeCompressibility: "1",
  };
}

/** 단위 전환 시 계산기 안의 압력 입력도 함께 환산한다. */
export function convertRateCalc(
  s: RateCalcState,
  from: PressureUnit,
  to: PressureUnit,
): RateCalcState {
  if (from === to) return s;
  const next = { ...s };
  for (const key of RATE_CALC_PRESSURE_FIELDS) {
    next[key] = convertInput(s[key], from, to);
  }
  return next;
}

/* ── 실행 ────────────────────────────────────────────────── */

export interface RateCalcContext {
  /** P1 — 배출 압력 (kPa 절대). 설정압·허용 과압에서 자동으로 정해진다. */
  readonly relievingPressureKpaAbs: number;
  /** 화면에서 고른 압력 단위 (계산기의 게이지압 입력을 해석하는 데 쓴다) */
  readonly unit: PressureUnit;
  /** 시나리오 행의 유체 상태 — 결과를 어떤 단위로 넘길지 정한다. */
  readonly fluidKind: SimpleFluidKind;
  /** 기체 되돌림 계산에 필요한 물성 (시나리오 행에서 가져온다) */
  readonly gas: {
    readonly temperatureK: number;
    readonly molecularWeight: number;
    readonly specificHeatRatio: number;
    readonly compressibility: number;
    readonly backpressureKpaAbs: number;
  };
}

/**
 * 시나리오 행과 밸브 상태에서 계산기 배경값을 만든다.
 *
 * 배출 압력 P1 은 사용자가 다시 넣게 하지 않는다 — 설정압과 허용 과압에서
 * 이미 정해지는 값이라 따로 받으면 어긋날 수 있다.
 */
export function rateCalcContext(
  r: {
    readonly contingency: ContingencyKind;
    readonly fluidKind: SimpleFluidKind;
    readonly temperature: string;
    readonly molecularWeight: string;
    readonly specificHeatRatio: string;
    readonly compressibility: string;
    readonly backpressure: string;
  },
  c: {
    readonly setPressure: string;
    readonly installation: InstallationKind;
  },
  unit: PressureUnit,
): RateCalcContext {
  return {
    relievingPressureKpaAbs: relievingPressureKpaAbs(
      toKpa(num(c.setPressure), unit),
      allowableOverpressurePercent(r.contingency, c.installation),
    ),
    unit,
    fluidKind: r.fluidKind,
    gas: {
      temperatureK: num(r.temperature),
      molecularWeight: num(r.molecularWeight),
      specificHeatRatio: num(r.specificHeatRatio),
      compressibility: num(r.compressibility),
      backpressureKpaAbs: toKpa(num(r.backpressure), unit) + ATMOSPHERIC_KPA,
    },
  };
}

export interface RateCalcLine {
  readonly label: string;
  readonly value: string;
}

export interface RateCalcOutcome {
  readonly ok: boolean;
  readonly kind: RateCalcKind;
  /** 실패 사유 (ok=false 일 때) */
  readonly message?: string;
  /** 화면에 보여줄 중간값·결과 */
  readonly lines: readonly RateCalcLine[];
  /** 적용한 식 (근거 표기용) */
  readonly equation?: string;
  /**
   * '소요 분출용량' 칸에 넣을 값. null 이면 넣을 수 없다는 뜻이다
   * (화재 기체 케이스는 표준이 유량이 아니라 면적을 준다).
   */
  readonly apply: {
    readonly value: string;
    readonly unit: "kg/h" | "L/min";
    readonly note?: string;
  } | null;
  readonly warnings: readonly string[];
}

const fmt = (v: number, digits = 1) =>
  v.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

function fail(kind: RateCalcKind, message: string): RateCalcOutcome {
  return { ok: false, kind, message, lines: [], apply: null, warnings: [] };
}

/** 화면이 고른 형상으로 습윤면적을 구한다. 'manual' 이면 사용자가 넣은 값. */
export function wettedAreaOf(s: RateCalcState): number {
  switch (s.geometry) {
    case "manual":
      return num(s.wettedArea);
    case "vertical":
      return verticalCylinderWettedArea(num(s.diameter), num(s.liquidHeight));
    case "horizontal":
      return horizontalCylinderWettedArea(
        num(s.diameter),
        num(s.length),
        num(s.liquidHeight),
      );
    case "sphere":
      return sphereWettedArea(num(s.diameter), num(s.liquidHeight));
  }
}

/**
 * 소요 면적을 API 520 기체식으로 되돌려 등가 유량을 구한다.
 *
 * 화재 기체 케이스(식 9)는 **면적을 바로 주므로** 지배 케이스 비교표에
 * 그대로 올릴 수 없다. 같은 배출 조건에서 그 면적을 채우는 유량으로
 * 되돌려야 다른 시나리오와 면적끼리 비교된다.
 *
 * ⚠ 이것은 API 521 의 식이 아니라 **API 520 기체식을 역으로 푼 것**이다.
 *   결과 화면에 그 사실을 반드시 표시한다.
 */
export function equivalentGasFlowKgH(
  requiredAreaMm2: number,
  g: RateCalcContext["gas"],
  p1KpaAbs: number,
  kd = 0.975,
): number | null {
  const { temperatureK: T, molecularWeight: M, specificHeatRatio: k } = g;
  const Z = g.compressibility;
  if (![T, M, k, Z].every((v) => Number.isFinite(v) && v > 0)) return null;
  if (k <= 1) return null;

  const ratio = g.backpressureKpaAbs / p1KpaAbs;
  const critical = ratio <= criticalPressureRatio(k);
  // A = W / (C·Kd·P1·Kb·Kc) × √(TZ/M)  →  W = A·C·Kd·P1 / √(TZ/M)
  // 아임계는 C 자리에 17.9·F2 가 들어간다(식 19). Kb=Kc=1 로 둔다.
  const coeff = critical
    ? coefficientC(k)
    : 17.9 * subcriticalCoefficientF2(k, ratio);
  const denom = Math.sqrt((T * Z) / M);
  if (!(denom > 0) || !(coeff > 0)) return null;
  return (requiredAreaMm2 * coeff * kd * p1KpaAbs) / denom;
}

/* ── 계산서용 산정 근거 ──────────────────────────────────── */

export interface BasisRow {
  readonly label: string;
  readonly value: string;
  /** 근거 조항. 입력값이면 "입력". */
  readonly source: string;
}

export interface RateCalcBasis {
  readonly title: string;
  readonly equation: string;
  /** 설계자가 넣은 값 */
  readonly inputs: readonly BasisRow[];
  /** 계산으로 나온 값 */
  readonly results: readonly BasisRow[];
  /**
   * 계산기로 넣은 값을 그 뒤에 손으로 고쳤으면 그 사실.
   * null 이면 근거와 계산서의 유량이 일치한다.
   */
  readonly mismatch: string | null;
  readonly warnings: readonly string[];
}

/**
 * 계산서에 실을 소요 분출용량 산정 근거.
 *
 * **계산기를 실제로 써서 값을 넣은 경우에만** 만든다(`appliedFlow`).
 * 손으로 넣은 유량에 계산기 입력창의 잔값으로 근거를 지어내면
 * 계산서가 거짓이 된다.
 */
export function rateCalcBasis(
  s: RateCalcState,
  ctx: RateCalcContext,
  /** 계산서에 실제로 실리는 유량 문자열 (시나리오 행의 값) */
  currentFlow: string,
): RateCalcBasis | null {
  if (s.appliedFlow.trim() === "") return null;

  const o = runRateCalc(s, ctx);
  if (!o.ok) return null;

  const u = (label: string) => `${label} (${ctx.unit === "kPa" ? "kPag" : ctx.unit + "g"})`;
  const inputs: BasisRow[] = [];

  switch (s.kind) {
    case "pdPumpLiquid":
      inputs.push(
        { label: "정격 토출량", value: `${s.pdRatedFlow} m³/h`, source: "입력" },
        { label: "밀도", value: `${s.pdDensity} kg/m³`, source: "입력" },
        { label: "계수", value: s.pdCapacityFactor, source: "설계자 지정" },
      );
      break;
    case "pdPumpGas":
      inputs.push(
        { label: "정격 토출량(흡입조건)", value: `${s.pdRatedFlow} m³/h`, source: "입력" },
        { label: u("흡입측 압력"), value: s.pdSuctionPressure, source: "입력" },
        { label: "흡입측 온도", value: `${s.pdSuctionTemperature} K`, source: "입력" },
        { label: "분자량", value: `${s.pdMolecularWeight} kg/kmol`, source: "입력" },
        { label: "압축계수 Z", value: s.pdCompressibility, source: "입력" },
        { label: "계수", value: s.pdCapacityFactor, source: "설계자 지정" },
      );
      break;
    case "pipingVelocity":
      inputs.push(
        { label: "관 내경", value: `${s.pipeInnerDiameter} mm`, source: "입력" },
        { label: "적용 유속", value: `${s.pipeVelocity} m/s`, source: "사내 설계기준" },
        { label: "밀도", value: `${s.pipeDensity} kg/m³`, source: "입력" },
      );
      break;
    case "pipeHydraulics":
      inputs.push(
        { label: "관 내경", value: `${s.phInnerDiameter} mm`, source: "입력" },
        { label: "직관 길이", value: `${s.phStraightLength} m`, source: "입력" },
        { label: "부속 등가길이", value: `${s.phFittingLength} m`, source: "입력" },
        { label: "절대조도", value: `${s.phRoughness} mm`, source: "입력" },
        { label: u("상류(구동측) 압력"), value: s.phUpstreamPressure, source: "입력" },
        { label: u("하류(받는측) 압력"), value: s.phDownstreamPressure, source: "입력" },
        { label: "밀도", value: `${s.phDensity} kg/m³`, source: "입력" },
        { label: "점도", value: `${s.phViscosity} cP`, source: "입력" },
      );
      break;
    case "cvValveLiquid":
      inputs.push(
        { label: "Cv", value: s.cvValue, source: "제작사 자료" },
        { label: u("상류(감압 전) 압력"), value: s.cvUpstreamPressure, source: "입력" },
        { label: u("하류(감압 후) 압력"), value: s.cvDownstreamPressure, source: "입력" },
        { label: "밀도", value: `${s.cvDensity} kg/m³`, source: "입력" },
        { label: "FL", value: s.cvFL, source: "제작사 자료(없으면 통상값)" },
      );
      break;
    case "cvValveGas":
      inputs.push(
        { label: "Cv", value: s.cvValue, source: "제작사 자료" },
        { label: u("상류(감압 전) 압력"), value: s.cvUpstreamPressure, source: "입력" },
        { label: u("하류(감압 후) 압력"), value: s.cvDownstreamPressure, source: "입력" },
        { label: "온도", value: `${s.cvTemperature} K`, source: "입력" },
        { label: "분자량", value: `${s.cvMolecularWeight} kg/kmol`, source: "입력" },
        { label: "비열비 k", value: s.cvSpecificHeatRatio, source: "입력" },
        { label: "압축계수 Z", value: s.cvCompressibility, source: "입력" },
        { label: "xT", value: s.cvXt, source: "제작사 자료(없으면 통상값)" },
      );
      break;
    case "valveFailureLiquid":
      inputs.push(
        { label: "관 내경", value: `${s.valveInnerDiameter} mm`, source: "입력" },
        { label: u("상류(감압 전) 압력"), value: s.valveUpstreamPressure, source: "입력" },
        { label: u("하류(감압 후) 압력"), value: s.valveDownstreamPressure, source: "입력" },
        { label: "밀도", value: `${s.valveDensity} kg/m³`, source: "입력" },
        { label: "배출계수 Cd", value: s.valveCd, source: "설계자 지정" },
      );
      break;
    case "valveFailureGas":
      inputs.push(
        { label: "관 내경", value: `${s.valveInnerDiameter} mm`, source: "입력" },
        { label: u("상류(감압 전) 압력"), value: s.valveUpstreamPressure, source: "입력" },
        { label: u("하류(감압 후) 압력"), value: s.valveDownstreamPressure, source: "입력" },
        { label: "온도", value: `${s.valveTemperature} K`, source: "입력" },
        { label: "분자량", value: `${s.valveMolecularWeight} kg/kmol`, source: "입력" },
        { label: "비열비 k", value: s.valveSpecificHeatRatio, source: "입력" },
        { label: "압축계수 Z", value: s.valveCompressibility, source: "입력" },
        { label: "배출계수 Cd", value: s.valveCd, source: "설계자 지정" },
      );
      break;
    case "fireWetted":
      inputs.push(
        {
          label: "용기 형상",
          value:
            s.geometry === "manual"
              ? "직접 입력"
              : s.geometry === "vertical"
                ? "수직 원통"
                : s.geometry === "horizontal"
                  ? "수평 원통"
                  : "구형",
          source: "입력",
        },
        ...(s.geometry === "manual"
          ? []
          : [
              { label: "안지름", value: `${s.diameter} m`, source: "입력" },
              ...(s.geometry === "horizontal"
                ? [{ label: "동체 길이", value: `${s.length} m`, source: "입력" }]
                : []),
              { label: "액위", value: `${s.liquidHeight} m`, source: "입력" },
            ]),
        {
          label: "화재 등급 보온",
          value: s.insulated ? "있음" : "없음",
          source: "Table 5",
        },
        {
          label: "배수·소방설비",
          value: s.drainage ? "적절함 (식 7)" : "부적절 (식 8)",
          source: "4.4.13.2.4.2",
        },
        { label: "증발잠열 λ", value: `${s.latentHeat} J/kg`, source: "입력" },
      );
      break;
    case "fireUnwetted":
      inputs.push(
        { label: "노출 표면적 A′", value: `${s.exposedArea} m²`, source: "입력" },
        { label: u("정상 운전 압력"), value: s.normalPressure, source: "입력" },
        { label: "정상 운전 온도 Tn", value: `${s.normalTemperature} K`, source: "입력" },
        { label: "최대 벽면 온도 Tw", value: `${s.wallTemperature} K`, source: "입력" },
        {
          label: "비열비 k",
          value: String(ctx.gas.specificHeatRatio),
          source: "시나리오 물성",
        },
      );
      break;
    case "thermal":
      inputs.push(
        { label: "체적팽창계수 αv", value: `${s.alphaV} 1/℃`, source: "Table 2" },
        { label: "총 전열량 φ", value: `${s.heatRate} W`, source: "입력" },
        { label: "비중 d", value: s.relativeDensity, source: "입력" },
        { label: "비열 c", value: `${s.specificHeat} J/kg·K`, source: "입력" },
      );
      break;
    case "tubeLiquid":
    case "tubeGas":
      inputs.push(
        { label: "튜브 안지름", value: `${s.tubeId} mm`, source: "입력" },
        { label: "개구부 수", value: "2개소", source: "4.4.14.2.2 c)" },
        { label: u("고압측 압력"), value: s.highSidePressure, source: "입력" },
        { label: "배출계수 Cd", value: s.tubeCd, source: "설계자 지정" },
        ...(s.kind === "tubeLiquid"
          ? [{ label: "고압측 밀도", value: `${s.tubeDensity} kg/m³`, source: "입력" }]
          : [
              { label: "고압측 온도", value: `${s.tubeTemperature} K`, source: "입력" },
              { label: "분자량 M", value: `${s.tubeMolecularWeight} kg/kmol`, source: "입력" },
              { label: "비열비 k", value: s.tubeSpecificHeatRatio, source: "입력" },
              { label: "압축계수 Z", value: s.tubeCompressibility, source: "입력" },
            ]),
      );
      break;
  }

  // 넣은 뒤에 손으로 고쳤는지 확인한다. 숫자로 비교해야
  // "8716" 과 "8716.0" 이 다르다고 오판하지 않는다.
  const a = Number.parseFloat(s.appliedFlow);
  const b = Number.parseFloat(currentFlow);
  const edited =
    !Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a - b) > 1e-9;

  return {
    title: RATE_CALC_LABELS[s.kind],
    equation: o.equation ?? "—",
    inputs,
    results: o.lines.map((l) => ({
      label: l.label,
      value: l.value,
      source: o.equation ?? "",
    })),
    mismatch: edited
      ? `계산기로 넣은 값은 ${s.appliedFlow} 이고, 계산서에 실린 값은 ${currentFlow} 입니다. ` +
        "넣은 뒤 설계자가 유량을 직접 고쳤습니다 — 아래 산정 근거는 참고용이며, " +
        "실제 적용값의 근거는 별도로 밝혀야 합니다."
      : null,
    warnings: o.warnings,
  };
}

export function runRateCalc(
  s: RateCalcState,
  ctx: RateCalcContext,
): RateCalcOutcome {
  const p1 = ctx.relievingPressureKpaAbs;

  switch (s.kind) {
    /* ── 정변위 펌프 최대유입 (액체) ── */
    case "pdPumpLiquid": {
      const r = positiveDisplacementRelievingRate({
        fluidState: "liquid",
        ratedVolumeFlowM3H: num(s.pdRatedFlow),
        densityKgM3: num(s.pdDensity),
        capacityFactor: num(s.pdCapacityFactor),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const volumeM3H = r.massFlowKgH / r.densityKgM3;
      const lines: RateCalcLine[] = [
        { label: "정격 토출량", value: `${s.pdRatedFlow} m³/h` },
        { label: "밀도", value: `${fmt(r.densityKgM3, 1)} kg/m³` },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "정변위 기계 최대유입 (일반 물성식 — 표준식 아님)",
        apply: liquid
          ? { value: ((volumeM3H * 1000) / 60).toFixed(2), unit: "L/min" }
          : null,
        warnings: liquid
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '액체'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 정변위 압축기 최대유입 (기체) ── */
    case "pdPumpGas": {
      const suctionAbs = toKpa(num(s.pdSuctionPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = positiveDisplacementRelievingRate({
        fluidState: "gas",
        ratedVolumeFlowM3H: num(s.pdRatedFlow),
        suctionPressureKpaAbs: suctionAbs,
        suctionTemperatureK: num(s.pdSuctionTemperature),
        molecularWeight: num(s.pdMolecularWeight),
        compressibility: num(s.pdCompressibility),
        capacityFactor: num(s.pdCapacityFactor),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "정격 토출량 (흡입조건)", value: `${s.pdRatedFlow} m³/h` },
        { label: "흡입측 절대압", value: `${fmt(suctionAbs)} kPa abs` },
        { label: "흡입측 밀도", value: `${fmt(r.densityKgM3, 3)} kg/m³` },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const gas = ctx.fluidKind === "gas";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "정변위 기계 최대유입 (일반 물성식 — 표준식 아님)",
        apply: gas ? { value: r.massFlowKgH.toFixed(1), unit: "kg/h" } : null,
        warnings: gas
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '기체·증기'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 배관 최대유입 — 표준유속 기준 ── */
    case "pipingVelocity": {
      const r = pipingVelocityInflowRate({
        innerDiameterMm: num(s.pipeInnerDiameter),
        velocityMs: num(s.pipeVelocity),
        densityKgM3: num(s.pipeDensity),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "관 내경", value: `${s.pipeInnerDiameter} mm` },
        { label: "적용 유속", value: `${s.pipeVelocity} m/s` },
        { label: "체적유량", value: `${fmt(r.volumeFlowM3H, 2)} m³/h` },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "배관 최대유입 (사내 설계유속 기준 — 표준식 아님)",
        apply: liquid
          ? { value: ((r.volumeFlowM3H * 1000) / 60).toFixed(2), unit: "L/min" }
          : { value: r.massFlowKgH.toFixed(1), unit: "kg/h" },
        warnings: r.warnings,
      };
    }

    /* ── 배관 최대유입 — 배관 수력학 역산 ── */
    case "pipeHydraulics": {
      const upAbs = toKpa(num(s.phUpstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const downAbs = toKpa(num(s.phDownstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = pipeInflowFromPressureDifference({
        innerDiameterMm: num(s.phInnerDiameter),
        straightLengthM: num(s.phStraightLength),
        fittingEquivalentLengthM: num(s.phFittingLength),
        roughnessMm: num(s.phRoughness),
        upstreamPressureKpaAbs: upAbs,
        downstreamPressureKpaAbs: downAbs,
        densityKgM3: num(s.phDensity),
        viscosityPaS: num(s.phViscosity) / 1000,
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "상류(구동측) 절대압", value: `${fmt(upAbs)} kPa abs` },
        { label: "하류(받는측) 절대압", value: `${fmt(downAbs)} kPa abs` },
        { label: "유속", value: `${fmt(r.velocityMs, 2)} m/s` },
        { label: "레이놀즈수", value: `${r.reynolds.toExponential(2)}` },
        { label: "마찰계수 f", value: r.frictionFactor.toFixed(4) },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "배관 수력학 역산 (Darcy–Weisbach — 표준식 아님)",
        apply: liquid
          ? { value: ((r.massFlowKgH / num(s.phDensity)) * (1000 / 60)).toFixed(2), unit: "L/min" }
          : { value: r.massFlowKgH.toFixed(1), unit: "kg/h" },
        warnings: r.warnings,
      };
    }

    /* ── 밸브 고장 — Cv 정식 계산 (액체) ── */
    case "cvValveLiquid": {
      const upAbs = toKpa(num(s.cvUpstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const downAbs = toKpa(num(s.cvDownstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const pcInput = s.cvCriticalPressure.trim();
      const r = cvValveFlowLiquid({
        cv: num(s.cvValue),
        upstreamPressureKpaAbs: upAbs,
        downstreamPressureKpaAbs: downAbs,
        densityKgM3: num(s.cvDensity),
        fl: num(s.cvFL),
        vaporPressureKpaAbs: num(s.cvVaporPressure),
        ...(pcInput === "" ? {} : { criticalPressureKpaAbs: num(s.cvCriticalPressure) }),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "상류(감압 전) 절대압", value: `${fmt(upAbs)} kPa abs` },
        { label: "하류(감압 후) 절대압", value: `${fmt(downAbs)} kPa abs` },
        { label: "적용 ΔP", value: `${fmt(r.appliedPressureDropKpa)} kPa` },
        { label: "유동 형태", value: r.choked ? "초크(공동현상/플래싱)" : "비초크" },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "ANSI/ISA-75.01.01 · IEC 60534-2-1 (Cv, 표준식이나 KOSHA·API 521 식은 아님)",
        apply: liquid
          ? { value: ((r.massFlowKgH / num(s.cvDensity)) * (1000 / 60)).toFixed(2), unit: "L/min" }
          : null,
        warnings: liquid
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '액체'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 밸브 고장 — Cv 정식 계산 (기체) ── */
    case "cvValveGas": {
      const upAbs = toKpa(num(s.cvUpstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const downAbs = toKpa(num(s.cvDownstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = cvValveFlowGas({
        cv: num(s.cvValue),
        upstreamPressureKpaAbs: upAbs,
        downstreamPressureKpaAbs: downAbs,
        temperatureK: num(s.cvTemperature),
        molecularWeight: num(s.cvMolecularWeight),
        specificHeatRatio: num(s.cvSpecificHeatRatio),
        compressibility: num(s.cvCompressibility),
        xT: num(s.cvXt),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "상류(감압 전) 절대압", value: `${fmt(upAbs)} kPa abs` },
        { label: "하류(감압 후) 절대압", value: `${fmt(downAbs)} kPa abs` },
        { label: "팽창계수 Y", value: r.expansionFactorY.toFixed(3) },
        { label: "유동 형태", value: r.choked ? "임계(초크)" : "아임계" },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const gas = ctx.fluidKind === "gas";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "ANSI/ISA-75.01.01 · IEC 60534-2-1 (Cv, 표준식이나 KOSHA·API 521 식은 아님)",
        apply: gas ? { value: r.massFlowKgH.toFixed(1), unit: "kg/h" } : null,
        warnings: gas
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '기체·증기'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 제어밸브·체크밸브 고장 (액체) ── */
    case "valveFailureLiquid": {
      const upAbs = toKpa(num(s.valveUpstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const downAbs = toKpa(num(s.valveDownstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = valveFailureLiquidRate({
        innerDiameterMm: num(s.valveInnerDiameter),
        upstreamPressureKpaAbs: upAbs,
        downstreamPressureKpaAbs: downAbs,
        densityKgM3: num(s.valveDensity),
        dischargeCoefficient: num(s.valveCd),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "상류(감압 전) 절대압", value: `${fmt(upAbs)} kPa abs` },
        { label: "하류(감압 후) 절대압", value: `${fmt(downAbs)} kPa abs` },
        { label: "유속", value: `${fmt(r.velocityMs, 2)} m/s` },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "오리피스 유동식 (베르누이 — 표준식 아님)",
        apply: liquid
          ? { value: ((r.massFlowKgH / num(s.valveDensity)) * (1000 / 60)).toFixed(2), unit: "L/min" }
          : null,
        warnings: liquid
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '액체'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 제어밸브·체크밸브 고장 (기체) ── */
    case "valveFailureGas": {
      const upAbs = toKpa(num(s.valveUpstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const downAbs = toKpa(num(s.valveDownstreamPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = valveFailureGasRate({
        innerDiameterMm: num(s.valveInnerDiameter),
        upstreamPressureKpaAbs: upAbs,
        downstreamPressureKpaAbs: downAbs,
        temperatureK: num(s.valveTemperature),
        molecularWeight: num(s.valveMolecularWeight),
        specificHeatRatio: num(s.valveSpecificHeatRatio),
        compressibility: num(s.valveCompressibility),
        dischargeCoefficient: num(s.valveCd),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "상류(감압 전) 절대압", value: `${fmt(upAbs)} kPa abs` },
        { label: "하류(감압 후) 절대압", value: `${fmt(downAbs)} kPa abs` },
        { label: "유동 형태", value: r.critical ? "임계(초크)" : "아임계" },
        { label: "소요 분출용량", value: `${fmt(r.massFlowKgH, 1)} kg/h` },
      ];

      const gas = ctx.fluidKind === "gas";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "압축성 오리피스 유동식 (표준식 아님)",
        apply: gas ? { value: r.massFlowKgH.toFixed(1), unit: "kg/h" } : null,
        warnings: gas
          ? r.warnings
          : [
              ...r.warnings,
              "시나리오 유체 상태를 '기체·증기'로 두어야 값을 넣을 수 있습니다.",
            ],
      };
    }

    /* ── 화재: 액체가 있는 용기 ── */
    case "fireWetted": {
      const aws = wettedAreaOf(s);
      const r = fireWettedRelievingRate({
        wettedAreaM2: aws,
        environmentFactor: num(s.environmentFactor),
        adequateDrainageAndFirefighting: s.drainage,
        latentHeatJKg: num(s.latentHeat),
        insulated: s.insulated,
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "습윤면적 Aws", value: `${fmt(aws, 2)} m²` },
        { label: "적용 환경계수 F", value: String(r.appliedF) },
        { label: "적용 상수", value: r.constant.toLocaleString("ko-KR") },
        { label: "흡수열량 Q", value: `${fmt(r.heatAbsorptionW / 1000)} kW` },
        { label: "소요 분출용량", value: `${fmt(r.relievingRateKgH)} kg/h` },
      ];

      if (ctx.fluidKind === "liquid") {
        return {
          ok: true,
          kind: s.kind,
          lines,
          equation: r.equation,
          apply: null,
          warnings: [
            ...r.warnings,
            "이 계산은 증발한 **증기**의 유량입니다. 시나리오의 유체 상태를 " +
              "'기체·증기'(또는 수증기)로 바꾸어야 값을 넣을 수 있습니다.",
          ],
        };
      }

      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: r.equation,
        apply: { value: r.relievingRateKgH.toFixed(0), unit: "kg/h" },
        warnings: r.warnings,
      };
    }

    /* ── 화재: 기체만 있는 용기 ── */
    case "fireUnwetted": {
      const normalAbs = toKpa(num(s.normalPressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = fireUnwettedRequiredArea({
        exposedAreaM2: num(s.exposedArea),
        relievingPressureKpaAbs: p1,
        normalPressureKpaAbs: normalAbs,
        normalTemperatureK: num(s.normalTemperature),
        maxWallTemperatureK: num(s.wallTemperature),
        specificHeatRatio: ctx.gas.specificHeatRatio,
        useDefaultFPrime: s.useDefaultFPrime,
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "배출 압력 P1", value: `${fmt(p1)} kPa abs` },
        { label: "기체 온도 T1 (식 12)", value: `${fmt(r.gasTemperatureK)} K` },
        { label: "계수 C (식 11)", value: r.coefficientC.toFixed(5) },
        {
          label: "F′ (식 10)",
          value:
            fmt(r.fPrime) +
            (r.fPrimeSource === "minimum-182"
              ? " (하한 182 적용)"
              : r.fPrimeSource === "default-821"
                ? " (기본값)"
                : ""),
        },
        {
          label: "소요 유효 면적 A",
          value: `${fmt(r.requiredAreaMm2, 0)} mm²`,
        },
      ];

      const equiv = equivalentGasFlowKgH(r.requiredAreaMm2, ctx.gas, p1);
      const warnings = [...r.warnings];

      if (ctx.fluidKind !== "gas" || equiv === null) {
        return {
          ok: true,
          kind: s.kind,
          lines,
          equation: r.equation,
          apply: null,
          warnings: [
            ...warnings,
            "이 케이스는 면적이 결과이므로 분출용량 칸에 넣을 수 없습니다. " +
              "시나리오 유체 상태를 '기체·증기'로 두면 등가 유량으로 되돌려 드립니다.",
          ],
        };
      }

      lines.push({
        label: "등가 유량 (되돌림)",
        value: `${fmt(equiv, 0)} kg/h`,
      });

      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: r.equation,
        apply: {
          value: equiv.toFixed(0),
          unit: "kg/h",
          note: "면적을 API 520 기체식으로 되돌린 등가 유량입니다.",
        },
        warnings: [
          ...warnings,
          "표시된 등가 유량은 API 521 의 값이 아니라, 식 (9)로 구한 면적을 " +
            "**API 520 기체식으로 역산**한 것입니다(Kb=Kc=1, Kd=0.975). " +
            "비교표를 면적 기준으로 맞추기 위한 환산이며, 계산서에는 " +
            "면적 A 를 근거로 기재하십시오.",
        ],
      };
    }

    /* ── 액체 열팽창 ── */
    case "thermal": {
      const r = thermalExpansionRelievingRate({
        cubicExpansionCoefficient: num(s.alphaV),
        heatTransferRateW: num(s.heatRate),
        relativeDensity: num(s.relativeDensity),
        specificHeatJKgK: num(s.specificHeat),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "체적유량 q", value: `${r.volumeFlowM3S.toExponential(3)} m³/s` },
        { label: "체적유량", value: `${fmt(r.volumeFlowLpm, 3)} L/min` },
        { label: "질량유량", value: `${fmt(r.massFlowKgH, 2)} kg/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: r.equation,
        apply: liquid
          ? { value: r.volumeFlowLpm.toFixed(3), unit: "L/min" }
          : { value: r.massFlowKgH.toFixed(2), unit: "kg/h" },
        warnings: liquid
          ? r.warnings
          : [
              ...r.warnings,
              "열팽창은 액체 배출입니다. 시나리오 유체 상태를 '액체'로 두는 " +
                "편이 사이징 결과가 정확합니다.",
            ],
      };
    }

    /* ── 튜브 파열: 액체 ── */
    case "tubeLiquid": {
      const highAbs = toKpa(num(s.highSidePressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = tubeRuptureLiquidRate({
        tubeInnerDiameterMm: num(s.tubeId),
        highSidePressureKpaAbs: highAbs,
        lowSidePressureKpaAbs: p1,
        densityKgM3: num(s.tubeDensity),
        dischargeCoefficient: num(s.tubeCd),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "고압측 압력", value: `${fmt(highAbs)} kPa abs` },
        { label: "저압측 배출압 P1", value: `${fmt(p1)} kPa abs` },
        { label: "차압 ΔP", value: `${fmt(r.pressureDifferenceKpa)} kPa` },
        { label: "파단 면적 (2개소)", value: `${fmt(r.breakAreaMm2, 1)} mm²` },
        { label: "질량유량", value: `${fmt(r.massFlowKgH, 0)} kg/h` },
        { label: "체적유량", value: `${fmt(r.volumeFlowM3H, 2)} m³/h` },
      ];

      const liquid = ctx.fluidKind === "liquid";
      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "비압축성 오리피스식 (API 521 4.4.14.2.2)",
        apply: liquid
          ? { value: (r.volumeFlowM3H * (1000 / 60)).toFixed(1), unit: "L/min" }
          : { value: r.massFlowKgH.toFixed(0), unit: "kg/h" },
        warnings: r.warnings,
      };
    }

    /* ── 튜브 파열: 기체 ── */
    case "tubeGas": {
      const highAbs = toKpa(num(s.highSidePressure), ctx.unit) + ATMOSPHERIC_KPA;
      const r = tubeRuptureGasRate({
        tubeInnerDiameterMm: num(s.tubeId),
        highSidePressureKpaAbs: highAbs,
        lowSidePressureKpaAbs: p1,
        temperatureK: num(s.tubeTemperature),
        molecularWeight: num(s.tubeMolecularWeight),
        specificHeatRatio: num(s.tubeSpecificHeatRatio),
        compressibility: num(s.tubeCompressibility),
        dischargeCoefficient: num(s.tubeCd),
      });
      if (!r.ok) return fail(s.kind, r.message);

      const lines: RateCalcLine[] = [
        { label: "고압측 압력", value: `${fmt(highAbs)} kPa abs` },
        { label: "저압측 배출압 P1", value: `${fmt(p1)} kPa abs` },
        {
          label: "압력비 P2/P1",
          value: `${r.pressureRatio.toFixed(3)} (임계비 ${r.criticalPressureRatio.toFixed(3)})`,
        },
        { label: "유동 형태", value: r.critical ? "임계(초크)" : "아임계" },
        { label: "파단 면적 (2개소)", value: `${fmt(r.breakAreaMm2, 1)} mm²` },
        { label: "질량유량", value: `${fmt(r.massFlowKgH, 0)} kg/h` },
      ];

      return {
        ok: true,
        kind: s.kind,
        lines,
        equation: "압축성 오리피스식 (API 521 4.4.14.2.2)",
        apply:
          ctx.fluidKind === "liquid"
            ? null
            : { value: r.massFlowKgH.toFixed(0), unit: "kg/h" },
        warnings:
          ctx.fluidKind === "liquid"
            ? [
                ...r.warnings,
                "기체 유량은 액체 시나리오에 넣을 수 없습니다. 유체 상태를 " +
                  "'기체·증기'로 바꾸십시오.",
              ]
            : r.warnings,
      };
    }
  }
}
